'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import type { FormConfig } from '../types';
import type { WaitlistWidgetTemplate } from '@/features/widget-setup/types';
import type { WidgetFormInteractiveProps, WidgetFormSuccessData } from '@/features/widget-setup/components/WidgetFormTemplates';
import { WidgetTemplatePreview } from '@/features/widget-setup/components/WidgetTemplatePreview';
import { leadSubmitSchema } from '@/lib/validations/lead';

interface InteractiveWidgetFormProps {
    formConfig: FormConfig;
    primaryColor: string;
    formTemplate?: WaitlistWidgetTemplate;
    onSubmit?: (data: {
        email: string;
        firstName?: string;
        lastName?: string;
        phone?: string;
        customAnswer?: string;
        _honeypot?: string;
        _renderedAt?: number;
    }) => Promise<{ success: boolean; data?: any }>;
    hideChrome?: boolean;
    showQueuePosition?: boolean;
    showReferralAfterSubmit?: boolean;
}

export function InteractiveWidgetForm({
    formConfig,
    primaryColor,
    formTemplate = 'centered',
    onSubmit,
    hideChrome = true,
    showQueuePosition = true,
    showReferralAfterSubmit = true,
}: InteractiveWidgetFormProps) {
    const [values, setValues] = useState<Record<string, string>>({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        customAnswer: '',
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [error, setError] = useState('');
    const [successData, setSuccessData] = useState<WidgetFormSuccessData | null>(null);
    const [honeypot, setHoneypot] = useState('');
    const renderedAtRef = useRef<number>(Date.now());

    // Reset renderedAt on mount
    useEffect(() => {
        renderedAtRef.current = Date.now();
    }, []);

    const onFieldChange = useCallback((field: string, value: string) => {
        setValues(prev => ({ ...prev, [field]: value }));
        if (fieldErrors[field]) {
            setFieldErrors(prev => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    }, [fieldErrors]);

    const onFormSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setFieldErrors({});

        if (!onSubmit) return;

        const payload: Record<string, string | undefined> = { email: values.email };
        if (formConfig.collectFirstName) payload.firstName = values.firstName;
        if (formConfig.collectLastName) payload.lastName = values.lastName;
        if (formConfig.collectPhone) payload.phone = values.phone;
        if (formConfig.customQuestion) payload.customAnswer = values.customAnswer;

        const validation = leadSubmitSchema.safeParse(payload);
        if (!validation.success) {
            const flat = validation.error.flatten().fieldErrors;
            const nextErrors: Record<string, string> = {};
            (Object.entries(flat) as [string, string[] | undefined][]).forEach(([key, messages]) => {
                if (messages?.[0]) nextErrors[key] = messages[0];
            });
            setFieldErrors(nextErrors);
            return;
        }

        setSubmitting(true);
        try {
            const result = await onSubmit({
                email: values.email,
                firstName: formConfig.collectFirstName ? values.firstName : undefined,
                lastName: formConfig.collectLastName ? values.lastName : undefined,
                phone: formConfig.collectPhone ? values.phone : undefined,
                customAnswer: formConfig.customQuestion ? values.customAnswer : undefined,
                _honeypot: honeypot || undefined,
                _renderedAt: renderedAtRef.current,
            });

            if (result.success) {
                if (result.data) {
                    setSuccessData(result.data as WidgetFormSuccessData);
                }
                setSubmitted(true);
                setValues({ email: '', firstName: '', lastName: '', phone: '', customAnswer: '' });
            } else {
                setError('Something went wrong. Please try again.');
            }
        } catch {
            setError('Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    }, [values, formConfig, onSubmit]);

    const interactiveProps: WidgetFormInteractiveProps = {
        values,
        onFieldChange,
        onFormSubmit,
        submitting,
        submitted,
        fieldErrors,
        error,
        successMessage: formConfig.successMessage,
        hideChrome,
        successData,
        showQueuePosition,
        showReferralAfterSubmit,
        primaryColor,
    };

    return (
        <div className="mx-auto w-full max-w-xl">
            {/* Honeypot field — hidden from real users, catches bots */}
            <input
                type="text"
                name="_honeypot"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
            />
            <WidgetTemplatePreview
                template={formTemplate}
                props={{
                    primaryColor,
                    buttonText: formConfig.emailButtonText || 'Join Waitlist',
                    emailPrompt: formConfig.emailPrompt || 'Join Our Waitlist',
                    collectFirstName: formConfig.collectFirstName,
                    collectLastName: formConfig.collectLastName ?? false,
                    collectPhone: formConfig.collectPhone ?? false,
                    customQuestion: formConfig.customQuestion ?? null,
                    successMessage: formConfig.successMessage ?? null,
                    interactive: interactiveProps,
                }}
            />
        </div>
    );
}
